import React from 'react';
import ReactInputMask from 'react-input-mask';
import { Box, InputProps } from '@apia/theme';
import useCombinedRefs from '../../../hooks/useCombinedRefs';
import { isChild } from '../../../utils/dom';
import IconButton from '../buttons/IconButton';
import { TIconName, TIconType } from '../Icon';

export interface IField {
  className?: string;
  disabled?: boolean;
  isLoading?: boolean;
  readOnly?: boolean;
}

export interface IIconInput extends IField {
  additionalButtons?: JSX.Element;
  buttonProps?: Partial<React.ComponentProps<typeof IconButton>>;
  buttonRef?: React.RefObject<HTMLButtonElement>;
  hideButton?: boolean;
  icon?: TIconType | TIconName;
  inputProps?: Partial<InputProps>;
  inputRef?: React.RefObject<HTMLInputElement>;
  mask?: string;
  onClick?: (ev: React.MouseEvent) => void;
  onBlur?: React.FocusEventHandler;
  [key: string]: unknown;
}

const IconInput = ({
  additionalButtons,
  buttonProps,
  buttonRef,
  className,
  disabled,
  hideButton,
  icon = 'Search',
  inputProps,
  inputRef,
  isLoading,
  mask,
  onClick,
  onBlur,
  readOnly,
  ...rest
}: IIconInput) => {
  const { sx: inputSx, ...actualInputProps } = inputProps ?? {};
  const innerButtonRef = React.useRef<HTMLButtonElement>(null);
  const assignButtonRef = useCombinedRefs(buttonRef, innerButtonRef);

  const innerInputRef = React.useRef<HTMLInputElement>(null);
  const assignInputRef = useCombinedRefs(inputRef, innerInputRef);

  const [parentRef, setParentRef] = React.useState<HTMLElement | null>(null);
  const handleBlur = React.useCallback(
    (ev: React.FocusEvent) => {
      if (
        onBlur &&
        (!ev.relatedTarget ||
          (ev.relatedTarget &&
            !isChild(
              ev.relatedTarget as HTMLElement,
              (current) => current === parentRef,
            )))
      )
        onBlur(ev);
    },
    [onBlur, parentRef],
  );

  return (
    <Box
      {...window.getVariant('forms.iconInput')}
      className={`${className ?? ''} iconInput ${
        rest.error ? 'field__withError' : ''
      }`}
      ref={setParentRef}
    >
      <ReactInputMask
        mask={mask ?? ''}
        onBlur={handleBlur}
        disabled={isLoading || disabled}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ref={assignInputRef as any}
        readOnly={readOnly}
        sx={{ variant: 'forms.input', ...inputSx }}
        data-variant="forms.input"
        {...rest}
        {...actualInputProps}
        maxLength={mask ? undefined : inputProps?.maxLength}
      />
      {additionalButtons}
      {!hideButton && (
        <IconButton
          onBlur={handleBlur}
          onClick={onClick}
          icon={icon}
          disabled={readOnly || disabled}
          isLoading={isLoading}
          ref={assignButtonRef as React.Ref<HTMLButtonElement>}
          {...buttonProps}
        />
      )}
    </Box>
  );
};

export default IconInput;
